import React, { useRef, useState } from 'react';
import { Container, Form, Button, Card, Spinner, Row, Col } from 'react-bootstrap';
import {
  FaCloudUploadAlt,
  FaDownload,
  FaFileExcel,
  FaCheckCircle,
  FaTimes,
  FaExclamationTriangle,
  FaUserPlus,
  FaSyncAlt,
  FaTimesCircle
} from 'react-icons/fa';
import api from '../../service/api';
import { useToast } from '../../contexts/ToastContext';

const TEMPLATE_HREF = '/assets/voter-import-template.xlsx';
const TEMPLATE_NAME = 'voter-import-template.xlsx';

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isExcelFile = (file) => {
  if (!file) return false;
  const name = file.name.toLowerCase();
  return name.endsWith('.xlsx') || name.endsWith('.xls');
};

const ImportData = () => {
  const fileInputRef = useRef(null);
  const { showSuccess, showError, showWarning } = useToast();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importSummary, setImportSummary] = useState(null);

  const applyFile = (file) => {
    if (!file) return;
    if (!isExcelFile(file)) {
      showError('Please select an Excel file (.xlsx or .xls).');
      setSelectedFile(null);
      setImportSummary(null);
      return;
    }
    setSelectedFile(file);
    setImportSummary(null);
  };

  const handleFileChange = (event) => {
    applyFile(event.target.files?.[0]);
  };

  const clearFile = (event) => {
    event?.stopPropagation();
    setSelectedFile(null);
    setImportSummary(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    applyFile(event.dataTransfer.files?.[0]);
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      showError('Please select an Excel file to upload.');
      return;
    }

    setLoading(true);
    setImportSummary(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await api.post('/api/excel/import-users', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const text = response.data.message || 'File uploaded and data processed.';
      if (response.data.summary?.errors?.length > 0) {
        showWarning(text);
      } else {
        showSuccess(text);
      }

      setImportSummary(response.data.summary);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        'Failed to import data. Please check the file format and try again.';

      showError(errorMsg);

      if (error.response?.data?.summary) {
        setImportSummary(error.response.data.summary);
      } else {
        setImportSummary(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const dropzoneClass = [
    'import-dropzone',
    isDragOver ? 'is-dragover' : '',
    selectedFile ? 'has-file' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const errorCount = importSummary?.errors?.length || 0;

  return (
    <div className="min-vh-100 p-3 p-md-3 bg-light fade-in-up">
      <Container fluid className="mx-auto" style={{ maxWidth: '1280px' }}>
        <div className="bg-white mt-3 rounded-3 shadow-sm border p-4 mb-4">
          <h1 className="h2 fw-bold text-dark mb-1">Import Voter Data</h1>
          <p className="text-secondary mb-0 small">
            Download the template, fill in voter rows, then upload the Excel file.
          </p>
        </div>

        <Row className="g-4">
          <Col lg={4}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Body className="p-4 d-flex flex-column">
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary"
                    style={{ width: 48, height: 48 }}
                  >
                    <FaFileExcel size={22} />
                  </div>
                  <div>
                    <h2 className="h5 fw-bold text-dark mb-0">Excel template</h2>
                    <p className="text-secondary small mb-0">Headers only — add your rows below</p>
                  </div>
                </div>

                <ol className="import-steps ps-3 mb-4 text-secondary small">
                  <li className="mb-2">Download the template with the required header row.</li>
                  <li className="mb-2">Enter voter data in the same columns. Do not rename headers.</li>
                  <li>Save the file and upload it on the right.</li>
                </ol>

                <Button
                  as="a"
                  href={TEMPLATE_HREF}
                  download={TEMPLATE_NAME}
                  variant="outline-primary"
                  className="d-flex align-items-center justify-content-center gap-2 rounded-3 fw-semibold py-2 mb-3"
                >
                  <FaDownload />
                  Download Template
                </Button>

                <div className="import-note rounded-3 p-3 mt-auto">
                  <div className="d-flex gap-2">
                    <FaExclamationTriangle className="text-warning flex-shrink-0 mt-1" />
                    <p className="small text-slate-700 mb-0">
                    Please enter the data exactly according to the template; otherwise, it will not work properly.
                    </p>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={8}>
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Body className="p-4">
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success"
                    style={{ width: 48, height: 48 }}
                  >
                    <FaCloudUploadAlt size={22} />
                  </div>
                  <div>
                    <h2 className="h5 fw-bold text-dark mb-0">Upload Excel file</h2>
                    <p className="text-secondary small mb-0">.xlsx or .xls — one file at a time</p>
                  </div>
                </div>

                <Form>
                  <Form.Control
                    ref={fileInputRef}
                    id="formFile"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className="d-none"
                  />

                  <div
                    className={dropzoneClass}
                    role="button"
                    tabIndex={0}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {selectedFile ? (
                      <div className="d-flex align-items-center justify-content-between gap-3 w-100">
                        <div className="d-flex align-items-center gap-3 min-w-0">
                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success flex-shrink-0"
                            style={{ width: 44, height: 44 }}
                          >
                            <FaFileExcel size={20} />
                          </div>
                          <div className="min-w-0 text-start">
                            <div className="fw-semibold text-dark text-truncate">{selectedFile.name}</div>
                            <div className="text-secondary small">{formatFileSize(selectedFile.size)}</div>
                          </div>
                        </div>
                        <Button
                          variant="light"
                          size="sm"
                          className="rounded-circle border text-secondary"
                          onClick={clearFile}
                          aria-label="Remove selected file"
                        >
                          <FaTimes />
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-2">
                        <div
                          className="rounded-circle d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary mb-3"
                          style={{ width: 56, height: 56 }}
                        >
                          <FaCloudUploadAlt size={24} />
                        </div>
                        <p className="fw-semibold text-dark mb-1">
                          Drag and drop your Excel file here
                        </p>
                        <p className="text-secondary small mb-0">
                          or click to browse from your computer
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="d-flex flex-wrap align-items-center gap-3 mt-4">
                    <Button
                      variant="primary"
                      className="px-4 py-2 fw-semibold rounded-3 shadow-sm"
                      onClick={handleFileUpload}
                      disabled={!selectedFile || loading}
                    >
                      {loading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                          <span className="ms-2">Importing...</span>
                        </>
                      ) : (
                        'Import Data'
                      )}
                    </Button>
                    {selectedFile && !loading && (
                      <span className="text-secondary small">Ready to import {selectedFile.name}</span>
                    )}
                  </div>
                </Form>

                {importSummary && (
                  <div className="mt-4">
                    <h3 className="h6 fw-bold text-uppercase text-slate-500 ls-wide mb-3">Import summary</h3>
                    <Row className="g-3">
                      <Col sm={4}>
                        <Card className="border-0 shadow-sm rounded-4 p-3 d-flex flex-row align-items-center gap-3 h-100">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success"
                            style={{ width: 44, height: 44 }}
                          >
                            <FaUserPlus size={18} />
                          </div>
                          <div>
                            <div className="text-secondary small fw-bold text-uppercase ls-wider">Added</div>
                            <div className="h4 fw-bold text-dark mb-0">{importSummary.added ?? 0}</div>
                          </div>
                        </Card>
                      </Col>
                      <Col sm={4}>
                        <Card className="border-0 shadow-sm rounded-4 p-3 d-flex flex-row align-items-center gap-3 h-100">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary"
                            style={{ width: 44, height: 44 }}
                          >
                            <FaSyncAlt size={16} />
                          </div>
                          <div>
                            <div className="text-secondary small fw-bold text-uppercase ls-wider">Updated</div>
                            <div className="h4 fw-bold text-dark mb-0">{importSummary.updated ?? 0}</div>
                          </div>
                        </Card>
                      </Col>
                      <Col sm={4}>
                        <Card className="border-0 shadow-sm rounded-4 p-3 d-flex flex-row align-items-center gap-3 h-100">
                          <div
                            className={`rounded-circle d-flex align-items-center justify-content-center ${errorCount ? 'bg-danger bg-opacity-10 text-danger' : 'bg-secondary bg-opacity-10 text-secondary'}`}
                            style={{ width: 44, height: 44 }}
                          >
                            {errorCount ? <FaTimesCircle size={18} /> : <FaCheckCircle size={18} />}
                          </div>
                          <div>
                            <div className="text-secondary small fw-bold text-uppercase ls-wider">Row errors</div>
                            <div className="h4 fw-bold text-dark mb-0">{errorCount}</div>
                          </div>
                        </Card>
                      </Col>
                    </Row>

                    {errorCount > 0 && (
                      <div className="mt-4">
                        <h6 className="text-danger fw-semibold mb-2">Detailed errors</h6>
                        <div className="import-error-list rounded-3 border bg-white">
                          <ul className="list-group list-group-flush">
                            {importSummary.errors.map((err, index) => (
                              <li key={index} className="list-group-item px-3 py-2">
                                <strong>Row {err.row}:</strong>{' '}
                                <span className="text-danger">{err.message}</span>
                                {err.email && (
                                  <div className="small text-muted">Email attempted: {err.email}</div>
                                )}
                                {err.data && (
                                  <div className="small text-muted text-truncate" title={err.data}>
                                    Data: {err.data}
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ImportData;
